import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  EventEmitter,
  Output,
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { getVaultItems } from '@app/root-store/vault-store/vault.selector';
import { VaultStoreEffects } from '@app/root-store/vault-store/effects';
import { ManageSharedVaultMembersComponent } from '@app/modules/vault/components/dialogs/add-shared-vault/manage-shared-vault-members/manage-shared-vault-members.component';
import { MatDialog } from '@angular/material/dialog';
import { MyInformationManagerComponent } from '@app/modules/vault/components/access-manager/my-information-manager.component';
import { AddChecklistComponent } from '@app/modules/checklist/components/dialogs/add-checklist/add-checklist.component';
import { SharedVaultService } from '@app/shared/services/vault/shared-vault.service';
import { vaultStoreActions } from '@app/root-store';
import { RootStoreState } from '@app/root-store';
import { SharedConfirmationDialogComponent } from '@app/shared/component/shared-confirmation-dialog/shared-confirmation-dialog.component';
import { ToastrService } from 'ngx-toastr';
import { CommonService } from '@app/shared/services';
import { AddVaultDialogComponent } from '@app/shared/component/add-vault-dialog/add-vault-dialog.component';
import * as vaultActions from '@app/root-store/vault-store/actions';
import { Router } from '@angular/router';
import { MovedToSharedVault } from '@app/shared/services';
import { NotifyMoveToSharedVaultService } from '@app/shared/services';
import * as loginSelectors from '@app/root-store/login-store/login.selector';
import { HomeObsService } from '../../home-obs.service';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';
@AutoUnsubscribe()
@Component({
  selector: 'vault-card-list',
  templateUrl: './vault-card-list.component.html',
  // styleUrl: './vault-card-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VaultCardListComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isPersonalVaults?: boolean;
  @Input() isEntityVaults?: boolean;
  @Input() isAllVaults?: boolean;
  @Input() searchText?: string = '';
  @Input() isFromTemplateRegister?: boolean;
  @Input() isHideMatMenu?: boolean = false;
  @Input() selectedVaults;
  // Filter inputs
  @Input() typeList?: any[] = [];
  @Input() selectedTypeList?: any[] = [];
  @Input() dateAddedList?: any[] = [];
  @Input() selectedDateAddedList?: any[] = [];
  @Input() lastUpdatedList?: any[] = [];
  @Input() selectedLastUpdatedList?: any[] = [];
  @Input() showOnlyDuplicates?: boolean = false;
  @Input() vaultView?: boolean = false;
  @Input() allVaultsSelected?: boolean = false;
  @Output() setDiscoverySetupVault: EventEmitter<any> = new EventEmitter();
  @Output() selectedVaultsEmitter: EventEmitter<any> = new EventEmitter();

  personalSharedVaults$: Observable<any[]>;
  entityVaults$: Observable<any[]>;
  allVaults$: Observable<any[]>;

  @Input() cardList: any[] = [];
  private subscription: Subscription = new Subscription();
  vaultStateObject$: any;
  stateObject;
  screenWidth: number;
  resizeObservable$: Observable<Event>;
  currentUser;
  vaultUserId;

  // Sorting properties
  sortType = 'Name';
  sortedArrayByName = true;
  sortedArrayByItems = false;
  showNumberOfAvatar = 2;

  // Store original unfiltered data
  originalCardList = [];
  @Input() isVaultView?: boolean = false;
  constructor(
    private store: Store,
    private sharedVaultService: VaultStoreEffects,
    private detectChange: ChangeDetectorRef,
    private dialog: MatDialog,
    private sharedVaultsService: SharedVaultService,
    private store$: Store<RootStoreState.State>,
    private commonService: CommonService,
    private toastr: ToastrService,
    private router: Router,
    private movedToSharedVault: MovedToSharedVault,
    private notifyMoveToSharedVaultService: NotifyMoveToSharedVaultService,
    private homeObsService: HomeObsService
  ) {
    this.subscription.add(
      this.store.pipe(select(loginSelectors.getCurrentUserFullDetails)).subscribe((data) => {
        if (data) {
          this.currentUser = data;
          this.vaultUserId = data?.id;
          localStorage.setItem('VaultUserId', this.vaultUserId);
        }
      })
    );

    this.vaultStateObject$ = this.sharedVaultService.vaultObject;
    this.subscription.add(
      this.vaultStateObject$.subscribe((item) => {
        this.stateObject = item;
        if (this.stateObject && this.stateObject?.userId) {
          this.vaultUserId = this.stateObject.userId;
          localStorage.setItem('VaultUserId', this.stateObject.userId);
        }
        this.detectChange.markForCheck();
      })
    );
  }

  ngOnInit(): void {
    this.showNumberOfAvatar = this.isHideMatMenu ? 3 : 2;
    this.allVaults$ = this.store.select(getVaultItems('Last Updated'));

    this.personalSharedVaults$ = this.allVaults$.pipe(
      map((vaults) => vaults.filter((vault) => vault.type?.toLowerCase() === 'personal' || vault.type?.toLowerCase() === 'shared'))
    );

    this.entityVaults$ = this.allVaults$.pipe(
      map((vaults) =>
        vaults.filter(
          (vault) => vault.type?.toLowerCase() === 'holding' || vault.type?.toLowerCase() === 'trust' || vault.type?.toLowerCase() === 'entity'
        )
      )
    );

    this.updateCardList();

    this.screenWidth = window.innerWidth;
    this.resizeObservable$ = fromEvent(window, 'resize');
    this.subscription.add(
      this.resizeObservable$.subscribe((evt) => {
        this.screenWidth = evt.target['innerWidth'];
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Apply filters when any filter input changes (but not on first initialization if data isn't loaded yet)
    const hasFilterChanges =
      changes.selectedTypeList ||
      changes.selectedDateAddedList ||
      changes.selectedLastUpdatedList ||
      changes.showOnlyDuplicates ||
      changes.searchText;

    if (hasFilterChanges) {
      // Only apply filters if we have data or if this is not the first change
      const isFirstChange = Object.keys(changes).every((key) => changes[key].isFirstChange());
      if (!isFirstChange || (this.originalCardList && this.originalCardList.length > 0)) {
        console.warn('VaultCardList: ngOnChanges triggered, applying filters', {
          isFirstChange,
          hasData: this.originalCardList?.length > 0,
        });
        this.applyFilters();
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private updateCardList(): void {
    if (this.isPersonalVaults) {
      this.subscription.add(
        this.personalSharedVaults$.subscribe((vaults) => {
          this.originalCardList = this.enrichVaultData(vaults);
          this.applyFilters();
        })
      );
    } else if (this.isEntityVaults) {
      this.subscription.add(
        this.entityVaults$.subscribe((vaults) => {
          this.originalCardList = this.enrichVaultData(vaults);
          this.applyFilters();
        })
      );
    } else if (this.isAllVaults) {
      this.subscription.add(
        this.allVaults$.subscribe((vaults) => {
          this.originalCardList = this.enrichVaultData(vaults);
          this.applyFilters();
        })
      );
    }
  }

  private enrichVaultData(vaults: any[]): any[] {
    console.warn('VaultCardList: Enriching vault data:', vaults.length, 'vaults');
    const enriched = vaults.map((vault) => ({
      ...vault,
      name: vault.name ?? vault.vault_name,
      is_creator: vault.user_id === this.vaultUserId,
      ...this.computeDateGrouping(vault.updated_at || vault.created_at),
    }));
    console.warn('VaultCardList: Sample enriched vault:', enriched[0]);
    return enriched;
  }

  private computeDateGrouping(dateString: string): { updated_at_last_updated: string; formatted_updated_at: string } {
    if (!dateString) {
      return { updated_at_last_updated: '', formatted_updated_at: '' };
    }

    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let grouping = '';
    if (diffDays <= 1) {
      grouping = 'Today';
    } else if (diffDays <= 7) {
      grouping = 'Past 7 Days';
    } else if (diffDays <= 30) {
      grouping = 'Past 30 Days';
    } else if (date.getFullYear() === now.getFullYear()) {
      grouping = 'This Year';
    } else {
      grouping = 'Older';
    }

    // Format the date as a readable string
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    return {
      updated_at_last_updated: grouping,
      formatted_updated_at: formattedDate,
    };
  }

  private applyFilters(): void {
    // Don't apply filters if there's no data yet
    if (!this.originalCardList || this.originalCardList.length === 0) {
      // Keep existing cardList if it has data, otherwise set to empty
      if (!this.cardList || this.cardList.length === 0) {
        this.cardList = [];
      }
      return;
    }

    let filtered = [...this.originalCardList];

    // Filter by search text
    if (this.searchText && this.searchText.trim().length > 0) {
      const searchLower = this.searchText.toLowerCase().trim();
      filtered = filtered.filter((vault) => vault.name?.toLowerCase().includes(searchLower) || vault.type?.toLowerCase().includes(searchLower));
    }

    // Filter by type
    if (this.selectedTypeList && this.selectedTypeList.length > 0) {
      filtered = filtered.filter((vault) => {
        return this.selectedTypeList.some((type) => {
          // Handle both object format and string format, and case-insensitive comparison
          const typeValue = typeof type === 'object' ? type.value : type;
          return vault.type?.toLowerCase() === typeValue?.toLowerCase();
        });
      });
    }

    // Filter by date added
    if (this.selectedDateAddedList && this.selectedDateAddedList.length > 0) {
      filtered = filtered.filter((vault) => {
        return this.selectedDateAddedList.some((dateOption) => {
          const dateValue = typeof dateOption === 'object' ? dateOption.value : dateOption;
          return this.matchesDateFilter(vault.created_at, dateValue);
        });
      });
    }

    // Filter by last updated
    if (this.selectedLastUpdatedList && this.selectedLastUpdatedList.length > 0) {
      filtered = filtered.filter((vault) => {
        return this.selectedLastUpdatedList.some((dateOption) => {
          // Handle both object format and string format
          const dateValue = typeof dateOption === 'object' ? dateOption.value : dateOption;
          // Match by formatted date
          return vault.formatted_updated_at === dateValue;
        });
      });
    }

    // Filter by duplicates
    if (this.showOnlyDuplicates) {
      const nameGroups = new Map<string, any[]>();
      filtered.forEach((vault) => {
        const name = vault.name?.toLowerCase();
        if (!nameGroups.has(name)) {
          nameGroups.set(name, []);
        }
        nameGroups.get(name).push(vault);
      });

      // Keep only vaults that have duplicates
      const duplicateNames = Array.from(nameGroups.entries())
        .filter(([_, vaults]) => vaults.length > 1)
        .map(([name, _]) => name);

      filtered = filtered.filter((vault) => duplicateNames.includes(vault.name?.toLowerCase()));

      // Automatically sort by name when showing duplicates to group them together
      filtered = filtered.sort((a, b) => a.name?.toLowerCase().localeCompare(b.name?.toLowerCase()));
    }

    this.cardList = filtered;
    console.warn('VaultCardList: Applied filters - Original:', this.originalCardList.length, 'Filtered:', filtered.length, {
      searchText: this.searchText,
      selectedTypes: this.selectedTypeList,
      selectedDateAdded: this.selectedDateAddedList,
      selectedLastUpdated: this.selectedLastUpdatedList,
      showDuplicates: this.showOnlyDuplicates,
    });
    this.detectChange.detectChanges();
  }

  private matchesDateFilter(dateString: string, filterValue: string): boolean {
    if (!dateString) return false;

    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    switch (filterValue) {
      case 'All Time':
        return true;
      case 'Today':
        return diffDays <= 1;
      case 'Past 7 Days':
        return diffDays <= 7;
      case 'Past 30 Days':
        return diffDays <= 30;
      case 'This Year':
        return date.getFullYear() === now.getFullYear();
      default:
        return true;
    }
  }

  openVault(vault, event, i) {
    if (this.isFromTemplateRegister) {
      this.setDiscoverySetupVault.emit(vault);
    } else {
      this.homeObsService.handleFetchVaultSideNavState({ open: false });
      this.router.navigate([`/vault/${vault.id}`]);
    }
    if (this.vaultView) {
      if (vault.id === null) {
        this.store.dispatch(new vaultActions.SetVaultSuccessAction(null, vault.name));
      }
      this.store$.dispatch(new vaultStoreActions.DefaultFolderRequestClear());
      let path = event.path || (event.composedPath && event.composedPath());
      if (this.router.url.includes('network') && this.router.url.includes('admin=true')) {
        this.subscription.add(
          this.movedToSharedVault.sendMessage({
            vault: vault,
            isTrue: true,
          })
        );
      } else if (
        event &&
        !this.router.url.includes('network') &&
        path[0].tagName !== 'BUTTON' &&
        path[0].tagName !== 'SPAN' &&
        path[0].tagName !== 'P' &&
        path[2].id !== 'kinnect-checklist-folder-img'
      ) {
        this.subscription.add(this.notifyMoveToSharedVaultService.sendMessage(true, vault, i));
      }
    }
  }

  getValutIcon(type: string) {
    if (type === 'personal') {
      return '/assets/images/vault/personal.svg';
    } else if (type === 'shared') {
      return '/assets/images/vault/shared.svg';
    } else if (type === 'holding' || type === 'trust' || type === 'entity') {
      return '/assets/images/vault/entity.svg';
    }
    return '';
  }

  isDuplicateVault(vault: any): boolean {
    if (!vault || !vault.name || !this.originalCardList) {
      return false;
    }

    const vaultName = vault.name.toLowerCase();
    const duplicateCount = this.originalCardList.filter((v) => v.name && v.name.toLowerCase() === vaultName).length;

    return duplicateCount > 1;
  }

  addNewVault() {
    const modalRef = this.dialog.open(AddVaultDialogComponent, {
      panelClass: 'full-window-dialog-2',
      disableClose: true,
      autoFocus: false,
      data: {
        user_id: this.vaultUserId,
        fromDiscovery: false,
        isFromPersonalVaults: this.isPersonalVaults,
        isFromEntityVaults: this.isEntityVaults,
      },
    });
    this.subscription.add(
      modalRef.afterClosed().subscribe((res) => {
        if (res.message) {
          this.store$.dispatch(new vaultStoreActions.GetVaultRequestAction(this.vaultUserId, false));
        }
      })
    );
  }

  openManageMembersDialog(vault: any) {
    let userId = this.vaultUserId;
    const dialogRef = this.dialog.open(ManageSharedVaultMembersComponent, {
      panelClass: 'full-window-dialog',
      autoFocus: false,
      data: { userId: userId, vaultId: vault.id, vaultName: vault.name, vaultType: vault.type, make_owner_enabled: true },
    });

    this.subscription.add(dialogRef.afterClosed().subscribe(() => {}));
  }

  manageSharing(vault) {
    const modalRef2 = this.dialog.open(MyInformationManagerComponent, {
      data: { vaultId: vault.id },
      autoFocus: false,
      panelClass: 'full-window-dialog-2',
    });

    this.subscription.add(modalRef2.afterClosed().subscribe(() => {}));
  }

  openDialogToRenameVault(vault) {
    let custom_name = 'Personal Vault Name';
    if (vault.type === 'shared') {
      custom_name = 'Shared Vault Name';
    } else if (vault.type === 'entity') {
      custom_name = 'Entity Vault Name';
    }
    const dialogRef = this.dialog.open(AddChecklistComponent, {
      width: '400px',
      maxWidth: this.screenWidth < 480 ? '90vw' : '80vw',
      data: { title: 'Vault Name', buttonName: 'Rename', currentChecklistName: vault.name, custom_name },
    });
    this.subscription.add(
      dialogRef.afterClosed().subscribe((res) => {
        if (res?.confirmation && res?.checklistName) {
          this.updateVaultName(vault.id, vault.user_id, res?.checklistName);
        }
      })
    );
  }

  updateVaultName(vaultId, vaultUserId, vaultName) {
    const payload = {
      id: vaultId,
      user_id: vaultUserId,
      name: vaultName,
    };

    this.subscription.add(
      this.sharedVaultsService.updateVaultName(payload).subscribe((res) => {
        if (res) {
          this.store$.dispatch(new vaultStoreActions.GetVaultRequestAction(this.vaultUserId, false));
        }
      })
    );
  }

  deleteVault(vault) {
    const selectedOption = 'Last Updated';
    const foreverDeletedRemainingDays = 7;
    let userCanDeleteVault = false;
    this.subscription.add(
      this.store.pipe(select(getVaultItems(selectedOption))).subscribe((item) => {
        const currentSharedVault = item.filter((item) => {
          return item.id == vault.id;
        });
        userCanDeleteVault = this.vaultUserId == currentSharedVault[0].vaultMembers[0].user_id ? true : false;
        this.detectChange.detectChanges();
      })
    );
    const sharedDialogRef = this.dialog.open(SharedConfirmationDialogComponent, {
      width: '400px',
      maxWidth: this.screenWidth < 480 ? '90vw' : '80vw',
      backdropClass: 'custom-box-shadow',
      hasBackdrop: true,
      data: {
        title: userCanDeleteVault ? 'Move to trash?' : 'Delete Permission Denied',
        materialIcon: 'delete',
        text: userCanDeleteVault
          ? `This vault will be deleted forever from your trash after ${foreverDeletedRemainingDays} days.`
          : 'Only an owner can delete their vault',
        proceedTitle: userCanDeleteVault ? 'Move to Trash' : '',
      },
    });
    this.subscription.add(
      sharedDialogRef.afterClosed().subscribe((confirmation) => {
        if (confirmation) {
          const modelRequest = {
            type: 'vault',
            ids: [vault.id],
          };

          this.subscription.add(
            this.commonService.moveVaultToTrash(modelRequest).subscribe((data) => {
              if (data) {
                this.store$.dispatch(new vaultStoreActions.GetVaultRequestAction(vault.user_id, false));
                this.toastr.success('Vault moved to trash successfully!');
              }
            })
          );
        }
      })
    );
  }

  sortData() {
    if (this.sortType == 'Name') {
      if (this.sortedArrayByName == false) {
        this.cardList = this.cardList
          .slice()
          .sort((a, b) => (a.name.toLowerCase() == b.name.toLowerCase() ? 0 : a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
        this.sortedArrayByName = !this.sortedArrayByName;
      } else {
        this.cardList = this.cardList
          .slice()
          .sort((a, b) => (a.name.toLowerCase() == b.name.toLowerCase() ? 0 : b.name.toLowerCase() > a.name.toLowerCase() ? 1 : -1));
        this.sortedArrayByName = !this.sortedArrayByName;
      }
    } else if (this.sortType == 'Items') {
      if (this.sortedArrayByItems == false) {
        this.cardList = this.cardList.slice().sort((a, b) => a.cnt - b.cnt);
        this.sortedArrayByItems = !this.sortedArrayByItems;
      } else {
        this.cardList = this.cardList.slice().sort((a, b) => b.cnt - a.cnt);
        this.sortedArrayByItems = !this.sortedArrayByItems;
      }
    }
    this.detectChange.detectChanges();
  }
  onVaultSelectedChange(event: any, vault: any): void {
    vault.selected = event.checked;
    // console.log({ cardList: this.cardList });
    // console.log({ selectedVaults: this.cardList.filter((v) => v.selected) });
    this.selectedVaultsEmitter.emit(this.cardList.filter((v) => v.selected));
    this.detectChange.detectChanges();
  }
}
